import React, { useMemo, useEffect, useState } from "react";
import { Link } from "react-router-dom";
//Import Breadcrumb
import { Container } from "reactstrap";
import "react-toastify/dist/ReactToastify.css";

import { toast } from "react-toastify";
import { Search, Upload } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
} from "../../components/shared/dialog";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/shared/alertDialog";

import Paginate from "../../components/shared/paginate";
import { X } from "lucide-react";
import {
  DownloadCSV,
  FetchReviews,
  SearchReviews,
  UpdateStatus,
} from "../../apis/Reviews";
import { useAuth } from "../../context/AuthContext";
import { PERMISSIONS } from "../../constants/permissions";

const ReviewsManagement = () => {
  const { hasPermission } = useAuth();
  const canManage = hasPermission(PERMISSIONS.MANAGE_USER_REVIEWS);
  const [currentPage, setCurrentPage] = useState("1");
  const [totalPages, setTotalPages] = useState("1");
  // const [users, setUsers] = useState([]);
  const [filteredReviews, setFilteredReviews] = useState([]);
  const [dataInCSV, setDataInCSV] = useState("");
  const [keyword, setKeyword] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [reasonError, setReasonError] = useState(false);
  const [openAlertDialog, setOpenAlertDialog] = useState(false);

  const limit = "10";

  const fetchReviews = async () => {
    try {
      const response = await FetchReviews(currentPage, limit); // Assuming FetchHouseTypes is the correct function from your API
      if (response.success) {
        setFilteredReviews(response?.data?.reviews);
        setTotalPages(response?.data?.totalPages);
      } else {
        toast.error("Failed to fetch reviews. Please try again later.");
      }
    } catch (error) {
      toast.error("Failed to fetch reviews. Please try again later.");
    }
  };

  const downloadCSV = () => {
    DownloadCSV().then((res) => {
      setDataInCSV(res);
    });
  };

  useEffect(() => {
    downloadCSV();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const searchReviews = async () => {
    try {
      const response = await SearchReviews(keyword, currentPage, limit);
      if (response.success) {
        setFilteredReviews(response?.data?.reviews);
        setTotalPages(response?.data?.totalPages);
      } else {
        toast.error("Failed to search reviews. Please try again later.");
      }
    } catch (error) {
      toast.error("Failed to search reviews. Please try again later.");
    }
  };

  useEffect(() => {
    if (keyword) {
      searchReviews();
    } else {
      fetchReviews();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, keyword]);

  useEffect(() => {
    if (keyword) {
      setCurrentPage("1");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword]);

  const columns = useMemo(
    () => [
      "ID",
      "Review content",
      "List title",
      "Reservation confirmation code",
      "checkIn",
      "Checkout",
      "Sender",
      "Receiver",
      "Review rating",
      "Review status",
      "Action",
    ],
    [],
  );

  const handleStatusChange = async (status, reviewId) => {
    try {
      const response = await UpdateStatus(reviewId, status);
      if (response?.success) {
        toast.success("Review status updated");
        const tempReviews = filteredReviews.map((review) => {
          if (review._id === reviewId)
            return {
              ...review,
              status,
            };
          else return review;
        });
        setFilteredReviews(tempReviews);
      } else toast.error("Failed to update review status");
    } catch (err) {
      toast.error("Failed to update review status");
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container
          //  h-[calc(100vh-130px)]
          className="px-0 dark:bg-darkBg1 dark:text-[#c7d8eb]"
          fluid
        >
          <div className="p-6 flex flex-col gap-6 ">
            <h1 className="text-2xl dark:text-[#c7d8eb]">Reviews management</h1>
            <div className="lg:flex flex-col lg:border lg:border-b-0 border-gray-400 dark:border-white">
              <div className="border-x border-t lg:border-none border-gray-500 dark:border-white rounded-[6px_6px_0px_0px] w-full flex flex-col lg:flex-row justify-between gap-4 p-4">
                <div className="relative border max-w-[400px] border-gray-500 dark:border-white rounded-md w-full">
                  <Search
                    size={16}
                    className="absolute left-2 bottom-1/2 translate-y-1/2"
                  />
                  <input
                    className="pl-8 pr-6 py-2 text-base rounded-md w-full dark:bg-darkBg2"
                    type="search"
                    placeholder="Search"
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                  />
                </div>
                <div className="flex lg:justify-self-end">
                  <a
                    href={`data:text/csv;charset=utf-8,${escape(dataInCSV)}`}
                    download="reviews-list.csv"
                  >
                    <div className="flex items-center gap-2">
                      Export data into CSV
                      <div className="shrink-0 bg-white p-1">
                        <Upload className="" size={16} />
                      </div>
                    </div>
                  </a>
                </div>
              </div>
              <div className="overflow-x-auto ">
                <div className="hidden lg:flex">
                  {columns.map((column, index) => (
                    <div
                      key={`column-${index}`}
                      className={`${
                        index === 0 ? "w-[12%]" : "w-[11%]"
                      } min-w-[130px] content-center border-y border-gray-500 dark:border-white break-words py-2 text-center font-semibold`}
                    >
                      {column}
                    </div>
                  ))}
                </div>
                {(filteredReviews &&
                  filteredReviews.length > 0 &&
                  filteredReviews.map((review, index) => (
                    <>
                      <div
                        key={`review-desktop-${index}`}
                        className="hidden lg:flex"
                      >
                        <div className="w-[10%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {review._id}
                        </div>
                        <div className="w-[9%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {review.description}
                        </div>
                        <div className="w-[9%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {review.property?.title}
                        </div>
                        <div className="w-[9%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {review.reservation?.code || "N/A"}
                        </div>
                        <div className="w-[9%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {review.reservation?.checkIn
                            ? review.reservation?.checkIn
                                .toString()
                                ?.slice(0, 10)
                            : "N/A"}{" "}
                        </div>
                        <div className="w-[9%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {review.reservation?.checkOut
                            ? review.reservation?.checkOut
                                .toString()
                                ?.slice(0, 10)
                            : "N/A"}{" "}
                        </div>
                        <Link
                          to={`${process.env.REACT_APP_CLIENTSIDE_URL}/users/show/${review.from?._id}`}
                          target="_blank"
                          className="w-[9%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center"
                        >
                          {review.from?.firstName}
                        </Link>
                        <Link
                          to={`${process.env.REACT_APP_CLIENTSIDE_URL}/users/show/${review.to?._id}`}
                          target="_blank"
                          className="w-[9%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center"
                        >
                          {review.to?.firstName}
                        </Link>
                        <div className="w-[9%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          <div className="flex mx-auto items-center justify-center">
                            {Array.from(
                              { length: review.rating || 0 },
                              (_, index) => (
                                <span
                                  key={index}
                                  className="text-xl text-red-600"
                                >
                                  ★
                                </span>
                              ),
                            )}
                          </div>
                        </div>
                        <div className="w-[9%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {canManage ? (
                            <select
                              id={`status-${review._id}`}
                              value={review.status}
                              onChange={(event) =>
                                handleStatusChange(
                                  event.target.value,
                                  review._id,
                                )
                              }
                              className="p-2 border border-gray-500 dark:border-white rounded-md dark:bg-darkBg2"
                            >
                              <option value={"disabled"}>Disabled</option>
                              <option value={"enabled"}>Enabled</option>
                            </select>
                          ) : (
                            <span
                              className={`px-2 py-1 rounded text-xs font-semibold ${
                                review.status === "enabled"
                                  ? "bg-green-500 text-white"
                                  : "bg-gray-500 text-white"
                              }`}
                            >
                              {review.status}
                            </span>
                          )}
                        </div>

                        <div className="w-[9%] min-w-[130px] px-1 content-center border-b border-gray-500 dark:border-white break-words py-2 text-center">
                          {canManage ? (
                            <Link
                              className="text-secondaryColor"
                              to={`/edit-review/${review._id}`}
                            >
                              Edit
                            </Link>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </div>
                      </div>
                      <div
                        key={`review-mobile-${index}`}
                        className="block lg:hidden mb-6 border-x border-y border-gray-500 dark:border-white"
                      >
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">List ID </div>
                          <div>{review._id}</div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Review content </div>
                          <div>{review.description}</div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">List title </div>
                          <div>{review.property?.title}</div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">
                            Reservation confirmation code
                          </div>
                          <div>{review.reservation?.code || "N/A"}</div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Check-in </div>
                          {review.reservation?.checkIn
                            ? review.reservation?.checkIn
                                .toString()
                                ?.slice(0, 10)
                            : "N/A"}{" "}
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Checkout </div>
                          {review.reservation?.checkOut
                            ? review.reservation?.checkOut
                                .toString()
                                ?.slice(0, 10)
                            : "N/A"}{" "}
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Sender</div>
                          <Link
                            to={`${process.env.REACT_APP_CLIENTSIDE_URL}/users/show/${review.from?._id}`}
                            target="_blank"
                          >
                            {review.from?.firstName}
                          </Link>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Receiver </div>
                          <Link
                            to={`${process.env.REACT_APP_CLIENTSIDE_URL}/users/show/${review.to?._id}`}
                          >
                            {review.to?.firstName}
                          </Link>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Review rating</div>
                          <div className="flex">
                            {Array.from(
                              { length: review.rating || 0 },
                              (_, index) => (
                                <span
                                  key={index}
                                  className="text-xl text-red-600"
                                >
                                  ★
                                </span>
                              ),
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Reviews status</div>
                          <div>
                            {canManage ? (
                              <select
                                value={review.status}
                                onChange={(event) =>
                                  handleStatusChange(
                                    event.target.value,
                                    review._id,
                                  )
                                }
                                className="p-2 border border-gray-500 dark:border-white rounded-md dark:bg-darkBg2"
                              >
                                <option value={"disabled"}>Disabled</option>
                                <option value={"enabled"}>Enabled</option>
                              </select>
                            ) : (
                              <span
                                className={`px-2 py-1 rounded text-xs font-semibold ${
                                  review.status === "enabled"
                                    ? "bg-green-500 text-white"
                                    : "bg-gray-500 text-white"
                                }`}
                              >
                                {review.status}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2 justify-between p-2 border-b border-gray-500 dark:border-white">
                          <div className="font-bold">Action</div>
                          <div>
                            {canManage ? (
                              <Link
                                className="text-secondaryColor"
                                to={`/edit-review/${review._id}`}
                              >
                                Edit
                              </Link>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </>
                  ))) || (
                  <div className="flex justify-center items-center h-20 border-x border-b border-gray-500 dark:border-white">
                    No matching records found.
                  </div>
                )}
              </div>
            </div>
            <Paginate
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalPages={totalPages}
            />
          </div>
          <Dialog open={openDialog} onOpenChange={setOpenDialog}>
            <DialogContent className="max-w-screen w-[calc(100vw-20px)] lg:ml-[120px] lg:w-[700px] max-h-[calc(100vh-calc(100vh/3))] overflow-auto select-none bg-gray-200 dark:bg-[#171d37] dark:text-[#c7d8eb]">
              <DialogHeader className={"flex flex-row"}>
                <h2 className="dark:text-[#c7d8eb] min-w-fit">
                  Decline Listing request
                </h2>
                <div className="w-full flex justify-end">
                  <X
                    className="cursor-pointer dark:text-[#c7d8eb]"
                    onClick={() => {
                      setOpenDialog(false);
                    }}
                    size={30}
                  />
                </div>
              </DialogHeader>
              <hr className="border-t-[rgba(0,0,0,0.6)] dark:border-t-[rgba(255,255,255,0.6)] -mt-3" />
              <div className="flex w-full flex-col">
                <h2 className="text-xl dark:text-[#c7d8eb]">
                  <label htmlFor="reason">Reason</label>
                </h2>
                <p className="text-red-500 mb-0">*Required</p>
                <p className="text-red-500">*At least 20 characters</p>
                <div className="w-full"></div>
                <div className="flex flex-col gap-1 mt-1">
                  {reasonError && (
                    <p className="text-red-500">This field is required</p>
                  )}
                </div>
              </div>
              <hr className="border-t-[rgba(0,0,0,0.6)] dark:border-t-[rgba(255,255,255,0.6)]" />
              <DialogFooter></DialogFooter>
            </DialogContent>
          </Dialog>
          <AlertDialog open={openAlertDialog} onOpenChange={setOpenAlertDialog}>
            <AlertDialogContent className="max-w-screen w-[calc(100vw-20px)] lg:w-[700px] max-h-[calc(100vh-calc(100vh/3))] overflow-auto select-none bg-gray-200 dark:bg-[#171d37] dark:text-[#c7d8eb]">
              <AlertDialogHeader>
                <AlertDialogTitle>
                  <h2 className="dark:text-[#c7d8eb] min-w-fit">
                    Are you sure you want to approve this listing
                  </h2>
                </AlertDialogTitle>
                <AlertDialogDescription>
                  Approving this listing will allow the host to list it for
                  clients
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter
                className={"flex justify-end gap-2 items-center"}
              >
                <AlertDialogCancel>
                  <div onClick={() => setOpenAlertDialog(false)}>Cancel</div>
                </AlertDialogCancel>
                <AlertDialogAction></AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ReviewsManagement;
